import { useEffect, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import api from "@/utils/axios";
import i18n from "@/i18n";
import { toast } from "react-toastify";
import { GENDERS, COUNTRIES } from "@/enums/MemberEnums";
import cls from "./MemberProfileEditPersonal.module.scss";

const MemberProfileEditPersonal = () => {
  const outletCtx = useOutletContext() || {};
  const { rawUsername } = outletCtx;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [memberMeta, setMemberMeta] = useState(null);
  const [realImage, setRealImage] = useState(null);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    gender: "",
    country: "",
    city: "",
    address: "",
    mobilePhone: "",
    workPlaceName: "",
    workPosition: "",
    educationLevel: "",
    universityNote: "",
    socialFacebook: "",
    socialInstagram: "",
    socialTikTok: "",
    socialYouTube: "",
  });

  const t = (key, vars) => i18n.t(`personalInfo.${key}`, vars);

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data } = await api.get("/v1/member/profile/personal");
        if (ignore) return;

        const { member, personal } = data || {};
        setMemberMeta(member || null);
        setRealImage(personal?.realImage || null);

        setForm((prev) => ({
          ...prev,
          firstName: personal?.firstName || "",
          lastName: personal?.lastName || "",
          gender: personal?.gender || "",
          country: personal?.country || "",
          city: personal?.city || "",
          address: personal?.address || "",
          mobilePhone: personal?.mobilePhone || "",
          workPlaceName: personal?.workPlaceName || "",
          workPosition: personal?.workPosition || "",
          educationLevel: personal?.educationLevel || "",
          universityNote: personal?.universityNote || "",
          socialFacebook: personal?.socialFacebook || "",
          socialInstagram: personal?.socialInstagram || "",
          socialTikTok: personal?.socialTikTok || "",
          socialYouTube: personal?.socialYouTube || "",
        }));
      } catch (err) {
        console.error("PersonalInfo load error", err);
        setError(err);
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    load();

    return () => {
      ignore = true;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    try {
      setSaving(true);
      setError(null);

      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        gender: form.gender,
        country: form.country,
        city: form.city,
        address: form.address,
        mobilePhone: form.mobilePhone,
        workPlaceName: form.workPlaceName,
        workPosition: form.workPosition,
        educationLevel: form.educationLevel,
        universityNote: form.universityNote,
        socialFacebook: form.socialFacebook,
        socialInstagram: form.socialInstagram,
        socialTikTok: form.socialTikTok,
        socialYouTube: form.socialYouTube,
      };

      const { data } = await api.patch("/v1/member/profile/personal", payload);

      const { member, personal } = data || {};
      setMemberMeta(member || null);
      setRealImage(personal?.realImage || null);

      toast.success(t("toastSaved"));
    } catch (err) {
      console.error("PersonalInfo save error", err);
      setError(err);
      toast.error(t("toastError"));
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };
  const headlineName = form.firstName || rawUsername || memberMeta?.id || "You";

  return (
    <section className={cls.page}>
      <header className={cls.header}>
        <button type="button" className={cls.backBtn} onClick={handleBack}>
          ← {t("memberProfileEditMember.back", { defaultValue: "Back" })}
        </button>
        <h1 className={cls.title}>{t("title")}</h1>
        <p className={cls.subtitle}>{t("subtitle", { name: headlineName })}</p>
      </header>

      {loading && (
        <div className={cls.stateBox}>
          <span className={cls.spinner} />
          <span>{t("loading")}</span>
        </div>
      )}

      {!loading && error && (
        <div className={cls.stateBoxError}>
          <p>{t("errorLoad")}</p>
        </div>
      )}

      {!loading && !error && (
        <form className={cls.form} onSubmit={handleSubmit}>
          <div className={cls.grid}>
            {/* RIGHT / SIDE COLUMN FIRST ON MOBILE (via CSS order) */}
            <aside className={cls.colSide}>
              <section className={cls.card}>
                <h2 className={cls.cardTitle}>
                  {t("sections.verification.title")}
                </h2>
                <p className={cls.cardHint}>
                  {t("sections.verification.hint")}
                </p>

                {realImage?.url ? (
                  <div className={cls.realImageBox}>
                    <div className={cls.realImageFrame}>
                      <img
                        src={realImage.url}
                        alt={t("sections.verification.alt")}
                        className={cls.realImage}
                      />
                    </div>
                    {realImage.updatedAt && (
                      <p className={cls.realImageMeta}>
                        {t("sections.verification.lastUpdated")}{" "}
                        {new Date(realImage.updatedAt).toLocaleString()}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className={cls.realImageEmpty}>
                    <p>{t("sections.verification.empty")}</p>
                  </div>
                )}

                <p className={cls.realImageNote}>
                  {t("sections.verification.note")}
                </p>
              </section>
            </aside>

            {/* LEFT / MAIN COLUMN */}
            <div className={cls.colMain}>
              {/* Legal name & gender */}
              <section className={cls.card}>
                <h2 className={cls.cardTitle}>
                  {t("sections.legalName.title")}
                </h2>
                <p className={cls.cardHint}>{t("sections.legalName.hint")}</p>

                <div className={cls.fieldsGrid}>
                  <div className={cls.fieldRow}>
                    <label className={cls.label} htmlFor="firstName">
                      {t("fields.firstName")}
                    </label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      className={cls.input}
                      value={form.firstName}
                      onChange={handleChange}
                      autoComplete="given-name"
                    />
                  </div>

                  <div className={cls.fieldRow}>
                    <label className={cls.label} htmlFor="lastName">
                      {t("fields.lastName")}
                    </label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      className={cls.input}
                      value={form.lastName}
                      onChange={handleChange}
                      autoComplete="family-name"
                    />
                  </div>

                  <div className={cls.fieldRowFull}>
                    <label className={cls.label} htmlFor="gender">
                      {t("fields.gender")}
                    </label>
                    <select
                      id="gender"
                      name="gender"
                      className={cls.input}
                      value={form.gender}
                      onChange={handleChange}
                    >
                      <option value="">{t("placeholders.select")}</option>
                      {GENDERS.map((g) => (
                        <option key={g} value={g}>
                          {t(`genders.${g}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </section>

              {/* Location & contact */}
              <section className={cls.card}>
                <h2 className={cls.cardTitle}>
                  {t("sections.location.title")}
                </h2>
                <p className={cls.cardHint}>{t("sections.location.hint")}</p>

                <div className={cls.fieldsGrid}>
                  <div className={cls.fieldRow}>
                    <label className={cls.label} htmlFor="country">
                      {t("fields.country")}
                    </label>
                    <select
                      id="country"
                      name="country"
                      className={cls.input}
                      value={form.country}
                      onChange={handleChange}
                    >
                      <option value="">{t("placeholders.select")}</option>
                      {COUNTRIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className={cls.fieldRow}>
                    <label className={cls.label} htmlFor="city">
                      {t("fields.city")}
                    </label>
                    <input
                      id="city"
                      name="city"
                      type="text"
                      className={cls.input}
                      value={form.city}
                      onChange={handleChange}
                    />
                  </div>

                  <div className={cls.fieldRowFull}>
                    <label className={cls.label} htmlFor="address">
                      {t("fields.address")}
                    </label>
                    <input
                      id="address"
                      name="address"
                      type="text"
                      className={cls.input}
                      value={form.address}
                      onChange={handleChange}
                    />
                  </div>

                  <div className={cls.fieldRowFull}>
                    <label className={cls.label} htmlFor="mobilePhone">
                      {t("fields.mobilePhone")}
                    </label>
                    <input
                      id="mobilePhone"
                      name="mobilePhone"
                      type="tel"
                      className={cls.input}
                      value={form.mobilePhone}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </section>

              {/* Social links */}
              <section className={cls.card}>
                <h2 className={cls.cardTitle}>{t("sections.socials.title")}</h2>
                <p className={cls.cardHint}>{t("sections.socials.hint")}</p>

                <div className={cls.fieldsGrid}>
                  <div className={cls.fieldRowFull}>
                    <label className={cls.label} htmlFor="socialFacebook">
                      {t("fields.socialFacebook")}
                    </label>
                    <input
                      id="socialFacebook"
                      name="socialFacebook"
                      type="url"
                      className={cls.input}
                      value={form.socialFacebook}
                      onChange={handleChange}
                    />
                  </div>

                  <div className={cls.fieldRowFull}>
                    <label className={cls.label} htmlFor="socialInstagram">
                      {t("fields.socialInstagram")}
                    </label>
                    <input
                      id="socialInstagram"
                      name="socialInstagram"
                      type="url"
                      className={cls.input}
                      value={form.socialInstagram}
                      onChange={handleChange}
                    />
                  </div>

                  <div className={cls.fieldRowFull}>
                    <label className={cls.label} htmlFor="socialTikTok">
                      {t("fields.socialTikTok")}
                    </label>
                    <input
                      id="socialTikTok"
                      name="socialTikTok"
                      type="url"
                      className={cls.input}
                      value={form.socialTikTok}
                      onChange={handleChange}
                    />
                  </div>

                  <div className={cls.fieldRowFull}>
                    <label className={cls.label} htmlFor="socialYouTube">
                      {t("fields.socialYouTube")}
                    </label>
                    <input
                      id="socialYouTube"
                      name="socialYouTube"
                      type="url"
                      className={cls.input}
                      value={form.socialYouTube}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </section>
            </div>
          </div>

          <div className={cls.actionsRow}>
            <button type="submit" className={cls.saveBtn} disabled={saving}>
              {saving ? t("saving") : t("saveChanges")}
            </button>
          </div>
        </form>
      )}
    </section>
  );
};

export default MemberProfileEditPersonal;
